import React, { useState } from "react";
import clientAxiosInstance from "../../api/clientAxiosInstance";
import {
  batchTotalExceedsMaxUploadSize,
  cumulativeMaxUploadExceededMessage,
  MAX_UPLOAD_FILE_SIZE_LABEL,
  sumFileSizesBytes,
} from "../../utils/uploadFileSizeLimit";

// Generate a valid UUID v4
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

const DocumentUploadPanel = ({ household_id, clientToken, disabled, onUploadConfirmed }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");

  /** @returns {string | null} error message or null if OK */
  const getFileValidationError = (file) => {
    const fileName = file.name.toLowerCase();
    const isValidType =
      ALLOWED_TYPES.includes(file.type) ||
      ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext));

    if (!isValidType) {
      return "Only PDF and image files (.pdf, .jpg, .jpeg, .png) are allowed";
    }
    return null;
  };

  const handleFiles = (files) => {
    const validFiles = [];
    const problems = [];

    for (const file of Array.from(files)) {
      const msg = getFileValidationError(file);
      if (msg) {
        problems.push(msg);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      if (problems.length) {
        setError([...new Set(problems)].join(" "));
      }
      return;
    }

    const existingFiles = selectedFiles.map(({ file: f }) => f);
    const mergedForLimit = [...existingFiles, ...validFiles];
    if (batchTotalExceedsMaxUploadSize(mergedForLimit)) {
      setError(
        cumulativeMaxUploadExceededMessage(sumFileSizesBytes(mergedForLimit)),
      );
      return;
    }

    if (problems.length) {
      setError([...new Set(problems)].join(" "));
    } else {
      setError("");
    }

    const newFiles = validFiles.map((file) => ({
      file,
      id: Date.now() + Math.random(),
    }));

    setSelectedFiles((prev) => [...prev, ...newFiles]);
  };

  const handleFileSelect = (event) => {
    handleFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const removeFile = (fileId) => {
    setSelectedFiles((prev) => prev.filter((item) => item.id !== fileId));
    setError("");
  };

  const getContentType = (file) => {
    const fileName = file.name.toLowerCase();
    if (file.type === "image/jpeg" || file.type === "image/jpg" || fileName.endsWith(".jpg") || fileName.endsWith(".jpeg")) {
      return "image/jpeg";
    }
    if (file.type === "image/png" || fileName.endsWith(".png")) {
      return "image/png";
    }
    return "application/pdf";
  };

  const uploadFiles = async () => {
    if (selectedFiles.length === 0) return;

    const fileList = selectedFiles.map(({ file }) => file);
    if (batchTotalExceedsMaxUploadSize(fileList)) {
      setError(
        cumulativeMaxUploadExceededMessage(sumFileSizesBytes(fileList)),
      );
      return;
    }

    try {
      setUploading(true);
      setError("");

      const batchId = generateUUID();
      const filesArray = selectedFiles.map(({ file }) => ({
        file_name: file.name,
        content_type: getContentType(file),
      }));

      // Step 1: Get presigned URLs
      const presignedResponse = await clientAxiosInstance.post(
        `/client/household/${household_id}/documents/presigned_urls`,
        { files: filesArray, batch_id: batchId },
        { headers: { Authorization: `Bearer ${clientToken}` } }
      );

      const { presigned_urls } = presignedResponse.data;

      if (!presigned_urls || presigned_urls.length !== selectedFiles.length) {
        throw new Error("Presigned URL mismatch");
      }

      // Step 2: Upload to S3 via XHR (for progress tracking)
      const uploadPromises = selectedFiles.map(({ file }, index) => {
        const presignedUrl = presigned_urls[index].presigned_url;
        // Use the content-type that S3 signed into the presigned URL — any
        // mismatch between this header and the signed value causes a 403.
        const contentType =
          new URL(presignedUrl).searchParams.get("content-type") ||
          getContentType(file);

        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: (e.loaded / e.total) * 100,
              }));
            }
          });
          xhr.addEventListener("load", () => {
            if (xhr.status === 200 || xhr.status === 204) resolve();
            else reject(new Error(`Upload failed for ${file.name}`));
          });
          xhr.addEventListener("error", () => reject(new Error(`Upload failed for ${file.name}`)));
          xhr.open("PUT", presignedUrl);
          xhr.setRequestHeader("Content-Type", contentType);
          xhr.send(file);
        });
      });

      await Promise.all(uploadPromises);

      // Step 3: Confirm upload
      await clientAxiosInstance.post(
        `/client/household/${household_id}/documents/confirm-upload`,
        { batch_id: batchId },
        { headers: { Authorization: `Bearer ${clientToken}` } }
      );

      setSelectedFiles([]);
      if (onUploadConfirmed) onUploadConfirmed();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress({});
    }
  };

  const hasFiles = selectedFiles.length > 0;

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="p-5">
        <h3 className="text-base font-bold text-gray-900 mb-4">Add New Document</h3>

        {/* Drop Zone */}
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
            isDragging
              ? "border-emerald-500 bg-emerald-50 scale-[1.01]"
              : "border-gray-300 hover:border-emerald-400 bg-gray-50/50"
          }`}
        >
          <input
            type="file"
            multiple
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={handleFileSelect}
            className="hidden"
            id="doc-upload-input"
            disabled={uploading || disabled}
          />
          <label htmlFor="doc-upload-input" className="cursor-pointer block">
            <div className={`w-14 h-14 mx-auto mb-3 rounded-2xl flex items-center justify-center transition-colors duration-200 ${
              isDragging ? "bg-emerald-200" : "bg-emerald-100"
            }`}>
              <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-900 mb-1">
              Drag & drop files here
            </p>
            <p className="text-xs text-gray-500 mb-3">
              Supported formats: PDF, JPG, PNG
              <br />
              Combined size must not exceed {MAX_UPLOAD_FILE_SIZE_LABEL} MB per
              upload
            </p>
          </label>
          <button
            onClick={() => document.getElementById("doc-upload-input").click()}
            disabled={uploading || disabled}
            className="px-5 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-white hover:border-gray-400 transition disabled:opacity-50 bg-white shadow-sm"
          >
            Browse Files
          </button>
        </div>

        {/* Selected Files */}
        {hasFiles && (
          <div className="mt-4 space-y-2">
            {selectedFiles.map((fileItem) => {
              const progress = uploadProgress[fileItem.file.name];
              const isUploading = uploading && progress !== undefined;

              return (
                <div
                  key={fileItem.id}
                  className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2.5 group"
                >
                  {/* File icon */}
                  <div className="w-8 h-8 rounded-lg bg-white border border-gray-200 flex items-center justify-center flex-shrink-0">
                    {fileItem.file.name.toLowerCase().endsWith(".pdf") ? (
                      <svg className="w-4 h-4 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 2l5 5h-5V4zM6 20V4h5v7h7v9H6z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    )}
                  </div>

                  {/* File info */}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-gray-800 truncate font-medium">{fileItem.file.name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(fileItem.file.size)}</p>
                  </div>

                  {/* Progress or remove */}
                  {isUploading ? (
                    <div className="w-20 flex-shrink-0">
                      <div className="bg-gray-200 rounded-full h-1.5 overflow-hidden">
                        <div
                          className="bg-emerald-500 h-1.5 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-[10px] text-gray-400 text-right mt-0.5">
                        {Math.round(progress)}%
                      </p>
                    </div>
                  ) : (
                    <button
                      onClick={() => removeFile(fileItem.id)}
                      className="text-gray-300 hover:text-red-500 transition flex-shrink-0 p-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              );
            })}

            {/* Upload / Uploading */}
            {!uploading ? (
              <button
                onClick={uploadFiles}
                disabled={disabled}
                className="w-full mt-2 px-4 py-2.5 bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-400 text-white rounded-lg text-sm font-semibold transition shadow-sm"
              >
                Upload {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}
              </button>
            ) : (
              <div className="flex items-center justify-center gap-2 mt-2 py-2 text-sm text-emerald-600 font-medium">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-500 border-t-transparent" />
                Uploading...
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-3 flex items-start gap-2 p-3 bg-red-50 rounded-lg border border-red-100">
            <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <p className="text-xs text-red-700">{error}</p>
          </div>
        )}
      </div>

      {/* Tips */}
      <div className="px-5 pb-5 space-y-2.5">
        <div className="flex items-start gap-2.5 p-3 bg-blue-50/70 rounded-lg">
          <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <p className="text-xs text-blue-800 leading-relaxed">
            Tip: Uploading clear, legible documents helps our AI extract data
            accurately, saving you time on manual entry.
          </p>
        </div>
        <div className="flex items-start gap-2.5 p-3 bg-blue-50/70 rounded-lg">
          <svg className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
          <p className="text-xs text-blue-800 leading-relaxed">
            Tip: After uploading documents, please wait until processing is
            complete before moving on to the next step.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadPanel;
